<script lang="ts">
  import { Markdown, Text } from '@lib/components';
  import { responsiveStyle, parseResponsiveCSS } from '@lib/directives';
  import type { ComparisonTableBlock, ComparisonHeaderConfig } from './types';

  export let block: ComparisonTableBlock;
  export let isFullscreen: boolean = false;

  $: responsiveCSS = block.css ? parseResponsiveCSS(block.css) : null;

  function getThemeColor(color: string) {
    const themeColors: Record<string, string> = {
      'primary': 'var(--theme-primary-color)',
      'secondary': 'var(--theme-secondary-color)',
      'tertiary': 'var(--theme-tertiary-color)',
      'accent': 'var(--theme-accent-color)',
      'success': 'var(--theme-success-color)',
      'warning': 'var(--theme-warning-color)',
      'error': 'var(--theme-error-color)',
      'info': 'var(--theme-info-color)'
    };
    return themeColors[color] || color;
  }

  function getHeaderConfig(header: string | ComparisonHeaderConfig): ComparisonHeaderConfig {
    if (typeof header === 'string') {
      return { text: header };
    }
    return header;
  }

  $: leftHeader = block.headers ? getHeaderConfig(block.headers.left) : null;
  $: rightHeader = block.headers ? getHeaderConfig(block.headers.right) : null;
</script>

<div
  class="comparison-table"
  class:center={block.alignment === 'center'}
  class:strikethrough={block.config?.strikethrough}
  use:responsiveStyle={responsiveCSS || {}}
>
  {#if block.pretitle}
    <p class="table-pretitle">{block.pretitle}</p>
  {/if}

  {#if block.title}
    <h2 class="table-title">{block.title}</h2>
  {/if}

  {#if block.subtitle}
    <p class="table-subtitle">{block.subtitle}</p>
  {/if}

  <div class="comparison-grid">
    <!-- Headers -->
    {#if leftHeader && rightHeader}
      <div
        class="comparison-header left"
        style:--header-text-color={leftHeader.textColor ? getThemeColor(leftHeader.textColor) : undefined}
        style:background-color={leftHeader.bgColor ? getThemeColor(leftHeader.bgColor) : undefined}
      >
        {leftHeader.text}
      </div>
      <div
        class="comparison-header right"
        style:--header-text-color={rightHeader.textColor ? getThemeColor(rightHeader.textColor) : undefined}
        style:background-color={rightHeader.bgColor ? getThemeColor(rightHeader.bgColor) : undefined}
      >
        {rightHeader.text}
      </div>
    {/if}

    <!-- Rows -->
    {#each block.rows as row, i}
      <div
        class="comparison-cell left"
        class:even={i % 2 === 0}
        style:--cell-text-color={block.columns?.left?.textColor ? getThemeColor(block.columns.left.textColor) : undefined}
        style:background-color={block.columns?.left?.bgColor ? getThemeColor(block.columns.left.bgColor) : undefined}
      >
        {row.left}
      </div>
      <div
        class="comparison-cell right"
        class:even={i % 2 === 0}
        style:--cell-text-color={block.columns?.right?.textColor ? getThemeColor(block.columns.right.textColor) : undefined}
        style:background-color={block.columns?.right?.bgColor ? getThemeColor(block.columns.right.bgColor) : undefined}
      >
        {row.right}
      </div>
    {/each}
  </div>
</div>

<style lang="postcss">
  .comparison-table {
    max-width: 1200px;
    margin: 0 auto;
  }

  .table-pretitle {
    font-size: 0.875rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    margin: 0 0 0.5rem 0;
    color: currentColor;
    opacity: 0.7;
    text-align: center;
  }

  .table-title {
    font-size: 1.8rem;
    font-weight: 700;
    line-height: 1.2;
    margin: 0 0 0.75rem 0;
    color: currentColor;
    text-align: center;
  }

  .table-subtitle {
    font-size: 1.25rem;
    font-weight: 400;
    line-height: 1.4;
    margin: 0 0 2rem 0;
    color: currentColor;
    opacity: 0.8;
    text-align: center;
  }

  @media (min-width: 768px) {
    .table-title {
      font-size: 2.25rem;
    }

    .table-subtitle {
      font-size: 1.4rem;
    }
  }

  .comparison-grid {
    display: grid;
    grid-template-columns: 1fr;
    gap: 0;
    border: 1px solid rgba(0, 0, 0, 0.1);
    border-radius: 4px;
    overflow: hidden;
  }

  :global([data-theme='dark']) .comparison-grid {
    border-color: rgba(255, 255, 255, 0.2);
  }

  .comparison-header {
    padding: 1.25rem 1.5rem;
    font-weight: 700;
    font-size: 1.125rem;
    text-align: center;
  }

  .comparison-header.left {
    background: var(--theme-primary-color, #007bff);
    color: var(--header-text-color, var(--theme-text-on-primary, white));
  }

  .comparison-header.right {
    background: var(--theme-secondary-color, #6c757d);
    color: var(--header-text-color, var(--theme-text-on-secondary, white));
  }

  .comparison-cell {
    padding: 1rem;
    border-top: 1px solid rgba(0, 0, 0, 0.1);
    display: flex;
    align-items: center;
    color: var(--cell-text-color, currentColor);
  }

  .comparison-cell:not([style*="background-color"]) {
    background: white;
  }

  :global([data-theme='dark']) .comparison-cell:not([style*="background-color"]) {
    background: rgba(255, 255, 255, 0.05);
    border-top-color: rgba(255, 255, 255, 0.1);
  }

  .comparison-cell.even:not([style*="background-color"]) {
    background: rgba(0, 0, 0, 0.02);
  }

  :global([data-theme='dark']) .comparison-cell.even:not([style*="background-color"]) {
    background: rgba(255, 255, 255, 0.08);
  }

  /* Only apply strikethrough when config enables it */
  .comparison-table.strikethrough .comparison-cell.left {
    text-decoration: line-through;
    text-decoration-thickness: 1px;
    text-decoration-color: rgba(0, 0, 0, 0.3);
  }

  :global([data-theme='dark']) .comparison-table.strikethrough .comparison-cell.left {
    text-decoration-color: rgba(255, 255, 255, 0.3);
  }

  /* Mobile: Side-by-side card pairs */
  @media (max-width: 767px) {
    .comparison-grid {
      grid-template-columns: 1fr 1fr;
      gap: 0;
    }

    .comparison-header {
      padding: 0.75rem 0.5rem;
      font-size: 0.875rem;
    }

    .comparison-header.left {
      opacity: 0.7;
      border-radius: 8px 0 0 0;
    }

    .comparison-header.right {
      border-radius: 0 8px 0 0;
    }

    .comparison-cell {
      padding: 0.875rem 0.75rem;
      font-size: 0.9rem;
      border-top: none;
      border-left: none;
      border-bottom: 1px solid rgba(0, 0, 0, 0.1);
      align-items: flex-start;
    }

    :global([data-theme='dark']) .comparison-cell {
      border-bottom-color: rgba(255, 255, 255, 0.1);
    }

    .comparison-cell.left {
      opacity: 0.6;
    }

    .comparison-cell.left:not([style*="background-color"]) {
      background: rgba(0, 0, 0, 0.02);
    }

    :global([data-theme='dark']) .comparison-cell.left:not([style*="background-color"]) {
      background: rgba(255, 255, 255, 0.03);
    }

    .comparison-cell.right {
      border-left: 3px solid var(--theme-secondary-color, #6c757d);
      font-weight: 500;
    }

    .comparison-cell.right:not([style*="background-color"]) {
      background: rgba(255, 255, 255, 0.5);
    }

    :global([data-theme='dark']) .comparison-cell.right:not([style*="background-color"]) {
      background: rgba(255, 255, 255, 0.08);
    }
  }

  /* Desktop: Side-by-side columns */
  @container (min-width: 768px) {
    .comparison-grid {
      grid-template-columns: 1fr 1fr;
    }

    .comparison-header.left {
      border-right: 1px solid rgba(255, 255, 255, 0.3);
    }

    .comparison-cell.left {
      border-right: 1px solid rgba(0, 0, 0, 0.1);
    }

    :global([data-theme='dark']) .comparison-cell.left {
      border-right-color: rgba(255, 255, 255, 0.1);
    }
  }

  .comparison-table.center {
    text-align: center;
  }

</style>
